<script setup lang="ts">
import CashbackItem from './CashbackItem.vue';
import { useLayoutStore } from '@/stores/layout';
import {
  CASHBACK_FALLBACK_URL,
  CASHBACK_ITEMS as items,
} from '@/utils/cashback-items';

const { isOpen, style } = defineProps<{ isOpen: boolean; style?: any }>();
const layoutStore = useLayoutStore();
const { isDesktop } = useLayout();

// Emits
const emit = defineEmits<{
  close: [];
  selectItem: [item: string];
}>();

// Função utilitária para gerar id a partir do nome (usada pelo filho)
const formatId = (name: string) => name.toLowerCase().replace(/\s+/g, '-');

const handleSelect = (payload: string | { url?: string }) => {
  // `payload` pode ser:
  // - uma URL (string começando com '/'),
  // - um id (string) — legacy (agora resolvido a partir de `items`),
  // - ou um objeto `item` emitido por `CashbackItem` (com `url`).
  if (typeof payload === 'string') {
    if (payload.startsWith('/')) {
      emit('selectItem', payload);
      emit('close');

      return;
    }

    // Procurar o item em `items` usando `formatId(name)` ou o próprio nome
    const found = items.find(
      (i) => formatId(i.name) === payload || i.name === payload,
    );

    const url = found?.url || CASHBACK_FALLBACK_URL;

    emit('selectItem', url);
    emit('close');

    return;
  }

  // payload é um objeto `item`
  const url = payload?.url || CASHBACK_FALLBACK_URL;

  emit('selectItem', url);
  emit('close');
};

const handleClose = () => {
  emit('close');
};

watch(
  () => isOpen,
  (val) => {
    if (isDesktop.value) {
      layoutStore.setCashbackMenuOpen(!!val);
    }
  },
  { immediate: true },
);
onUnmounted(() => {
  if (isDesktop.value) layoutStore.setCashbackMenuOpen(false);
});
</script>

<template>
  <!-- Dropdown / Modal teleported to body (desktop and mobile) -->

  <Teleport to="body">
    <!-- Desktop: dropdown ancorado pelo `style` -->
    <div
      v-if="isOpen && isDesktop"
      class="fixed select-none"
      :style="[{ zIndex: 100000 }, style]"
      @click.stop
    >
      <div
        class="bg-bg-primary border border-[#1d2329] border-solid flex flex-col gap-[15px] items-center justify-center px-5 py-5 relative rounded-[10px] shadow-[0px_8px_40px_rgba(0,0,0,0.6)] w-[360px]"
      >
        <!-- Wrapper para o Chevron para sobrepor a borda -->
        <div class="absolute -left-[28px] top-1/2 -translate-y-1/2 z-10">
          <NuxtIcon
            class="text-[34px] text-bg-primary"
            filled
            name="cashback-chevron"
          />
          <div
            class="bg-bg-primary rounded-[8px] w-2 h-[31px] absolute right-[1.3px] top-1/2 -translate-y-1/2 z-20"
          ></div>
        </div>

        <div class="flex items-center justify-between relative shrink-0 w-full">
          <p class="font-bold text-white text-[15px] leading-[15px]">
            {{ $t('account.wallet.cashback_options_title') }}
          </p>
          <button
            aria-label="Fechar"
            class="cursor-pointer h-5 w-5 relative shrink-0 flex items-center justify-center"
            @click="handleClose"
          >
            <NuxtIcon
              class="font-bold text-[20px] text-white"
              name="close"
            />
          </button>
        </div>

        <div
          v-for="item in items"
          :key="item.name"
          class="w-full"
        >
          <CashbackItem
            :item="item"
            @select="handleSelect"
          />
        </div>

        <div class="w-full">
          <button
            class="flex h-[38.405px] w-full justify-center items-center gap-[14.051px] self-stretch bg-bg-primary backdrop-blur-[9.367098808288574px] p-[18.734px] rounded-[9.367px] border-[0.937px] border-solid border-[#1D2329] text-white font-bold leading-[14.987px]"
            is-buttom
            :style="{ fontSize: '12px' }"
            @click="handleClose"
            >FECHAR</button
          >
        </div>
      </div>
    </div>

    <!-- Mobile: bottom sheet slide-up (igual MobilePreview) -->
    <div
      v-if="!isDesktop && isOpen"
      class="cb-overlay"
      @click.self="handleClose"
    >
      <div
        class="cb-backdrop"
        @click="handleClose"
      />
      <Transition
        appear
        name="cb-slide-up"
      >
        <div
          v-if="isOpen"
          class="cb-sheet bg-bg-primary"
        >
          <div class="cb-sheet-inner">
            <div
              class="flex items-center justify-between relative shrink-0 w-full"
            >
              <p class="font-bold text-white text-[15px] leading-[15px]">{{
                $t('account.wallet.cashback_options_title')
              }}</p>
              <button
                aria-label="Fechar"
                class="cursor-pointer h-5 w-5 relative shrink-0 flex items-center justify-center"
                @click="handleClose"
              >
                <NuxtIcon
                  class="font-bold text-[20px] text-white"
                  name="close"
                />
              </button>
            </div>

            <div
              v-for="item in items"
              :key="item.name"
              class="w-full"
            >
              <CashbackItem
                :item="item"
                @select="handleSelect"
              />
            </div>

            <div class="w-full">
              <button
                class="flex h-[38.405px] w-full justify-center items-center gap-[14.051px] self-stretch bg-bg-primary backdrop-blur-[9.367098808288574px] p-[18.734px] rounded-[9.367px] border-[0.937px] border-solid border-[#1D2329] text-white font-bold leading-[14.987px]"
                :style="{ fontSize: '12px' }"
                @click="handleClose"
                >FECHAR</button
              >
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<style scoped>
.cb-overlay {
  position: fixed;
  inset: 0;
  z-index: 100000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.cb-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
}

.cb-sheet {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 400px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  /* fundo: classe `bg-bg-primary` no template — o CSS com escopo tem
     especificidade maior que a utility e sobrescreveria o token. */
  border: 1px solid #1d2329;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
}

.cb-sheet-inner {
  display: flex;
  flex-direction: column;
  gap: 15px;
  padding: 20px 20px calc(20px + env(safe-area-inset-bottom, 0px));
}

.cb-slide-up-enter-active {
  animation: cb-slide-up-spring 0.32s cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
  will-change: transform, opacity;
}
.cb-slide-up-leave-active {
  animation: cb-slide-up-spring 0.22s cubic-bezier(0.4, 0, 1, 1) reverse
    forwards;
  will-change: transform, opacity;
}
@keyframes cb-slide-up-spring {
  0% {
    transform: translateY(100%);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}
</style>
